<template>
  <div class="replace-panel">
    <el-alert
      v-if="localOnly"
      title="已开启“不同步对端”"
      description="文件替换和 EXE 回滚只处理本端服务器，不会再同步另一台服务器。调试完成后请记得关闭此开关。"
      type="warning"
      show-icon
      :closable="false"
      class="local-only-alert"
    />

    <div class="workspace-grid two-cols">
      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span>选择上传文件</span>
            <el-switch
              v-model="localOnly"
              :disabled="loading || rollbackLoading"
              active-text="不同步对端"
              title="打开后：文件替换、EXE 回滚只处理本端服务器，不同步另一台服务器"
            />
          </div>
        </template>

        <el-radio-group v-model="uploadKind" class="kind-group">
          <el-radio-button label="exe">EXE 上传</el-radio-button>
          <el-radio-button label="other">其他配置文件</el-radio-button>
        </el-radio-group>

        <el-alert
          title="DLL 已从本页面移除，请使用“DLL 安全热更新”页面执行候选、自检、灰度和双服务器事务提交。"
          type="info"
          show-icon
          :closable="false"
          class="dll-alert"
        />

        <el-upload
          ref="uploadfiletest"
          class="upload-block"
          :auto-upload="false"
          :limit="1"
          :on-change="replaceFile"
          :on-exceed="replaceFileTest"
          :show-file-list="true"
          :accept="acceptByKind"
        >
          <template #trigger>
            <el-button type="primary">选择文件</el-button>
          </template>
          <el-button class="ml-12" @click="clearReplaceFile">清空</el-button>
        </el-upload>

        <div class="info-block">
          <div class="info-row">
            <span class="info-label">当前文件</span>
            <span class="info-value">{{ oldFile ? oldFile.name : '未选择文件' }}</span>
          </div>
          <div v-if="oldFile" class="info-row">
            <span class="info-label">文件大小</span>
            <span class="info-value">{{ formatFileSize(oldFile.size || 0) }}</span>
          </div>
        </div>
      </el-card>

      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header"><span>目标路径</span></div>
        </template>

        <div class="path-box">
          <el-input
            v-model="targetReplaceFilePath"
            :disabled="!pathEditable"
            placeholder="目标必须位于 C:\mazda 下"
          />
          <div class="path-actions">
            <el-button v-if="pathEditable" @click="showReplaceFileDialog">修改路径</el-button>
            <el-button :loading="queryFileSizeLoading" @click="queryCurrentTargetFileSize">查看当前文件大小</el-button>
            <el-button type="success" plain :loading="downloadLoading" @click="downloadTargetFile">下载该路径文件</el-button>
          </div>
        </div>

        <div class="tips-box">
          <div class="tip-line">1. EXE 固定使用 C:\mazda\tool_MAZDA.exe，上传前保留上一份用于回滚。</div>
          <div class="tip-line">2. 其他文件只能写入 C:\mazda 下，目标文件名必须与上传文件名一致。</div>
          <div class="tip-line">3. 服务端会拒绝 UNC、设备路径、ADS、..、目录连接点和符号链接逃逸。</div>
          <div class="tip-line">4. DLL 上传和 DLL 回滚只能在专用“DLL 安全热更新”页面操作。</div>
        </div>

        <div v-if="targetFileSizeQueried" class="info-block">
          <div class="info-row">
            <span class="info-label">目标文件状态</span>
            <span class="info-value">{{ targetFileExists ? '存在' : '不存在' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">当前文件大小</span>
            <span class="info-value">{{ targetFileExists ? formatFileSize(targetFileSizeBytes) : '-' }}</span>
          </div>
          <div v-if="targetFileQueryMessage" class="info-row">
            <span class="info-label">查询结果</span>
            <span class="info-value">{{ targetFileQueryMessage }}</span>
          </div>
          <div v-if="canRollback" class="info-row">
            <span class="info-label">上一份备份</span>
            <span class="info-value">{{ backupExists ? formatFileSize(backupSizeBytes) : '无' }}</span>
          </div>
        </div>

        <div class="action-row">
          <el-button type="primary" :loading="loading" @click="submitUpload">开始替换</el-button>
          <el-button
            v-if="canRollback"
            type="warning"
            plain
            :loading="rollbackLoading"
            :disabled="!backupExists"
            @click="rollbackCurrent"
          >
            回滚上一份 EXE
          </el-button>
          <el-button @click="clearReplaceFile">取消</el-button>
        </div>
      </el-card>
    </div>

    <el-dialog v-model="dialogExchangeFileName" title="修改目标文件路径" width="520px">
      <el-input v-model="editPath" placeholder="请输入 C:\mazda 下的完整文件路径" />
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="cancelInputReplacePath">取消</el-button>
          <el-button type="primary" @click="confirmReplaceFile">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { useMazdaReplace } from '@/composables/mazda/useMazdaReplace';

const props = defineProps({
  serverName: { type: String, default: '' },
  mazdaPost: { type: Function, required: true },
});

const {
  uploadKind,
  localOnly,
  acceptByKind,
  canRollback,
  pathEditable,
  targetReplaceFilePath,
  editPath,
  dialogExchangeFileName,
  uploadfiletest,
  oldFile,
  loading,
  queryFileSizeLoading,
  downloadLoading,
  rollbackLoading,
  targetFileSizeQueried,
  targetFileExists,
  targetFileSizeBytes,
  targetFileQueryMessage,
  backupExists,
  backupSizeBytes,
  formatFileSize,
  replaceFile,
  replaceFileTest,
  clearReplaceFile,
  showReplaceFileDialog,
  confirmReplaceFile,
  cancelInputReplacePath,
  queryCurrentTargetFileSize,
  downloadTargetFile,
  submitUpload,
  rollbackCurrent,
} = useMazdaReplace(props.mazdaPost);
</script>

<style scoped>
.workspace-grid.two-cols { display: grid; grid-template-columns: repeat(2, minmax(340px, 1fr)); gap: 22px; }
.local-only-alert { margin-bottom: 18px; border-radius: 14px; }
.workspace-card { border-radius: 24px; border: 1px solid rgba(225, 232, 242, 0.95); box-shadow: 0 12px 30px rgba(29, 46, 76, 0.07); }
.workspace-card :deep(.el-card__body) { padding-top: 16px; padding-bottom: 16px; }
.card-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; font-weight: 800; color: #1f2d3d; }
.kind-group { margin-bottom: 14px; }
.dll-alert { margin-bottom: 14px; }
.upload-block { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 6px 0 4px; }
.upload-block :deep(.el-button), .path-actions :deep(.el-button), .action-row :deep(.el-button), .dialog-footer :deep(.el-button) { min-width: 96px; }
.ml-12 { margin-left: 12px; }
.info-block { margin-top: 18px; padding: 16px 18px; border-radius: 16px; background: linear-gradient(180deg, #fbfcff, #f6f9ff); border: 1px solid #ecf2ff; }
.info-row { display: flex; gap: 12px; line-height: 1.9; }
.info-label { width: 90px; color: #8a94a6; flex-shrink: 0; }
.info-value { color: #2f3441; word-break: break-all; }
.path-box { display: flex; flex-direction: column; gap: 12px; }
.path-actions { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
.tips-box { margin-top: 18px; padding: 16px 18px; border-radius: 16px; background: linear-gradient(180deg, #fffaf0, #fff7e6); border: 1px solid #ffe4ad; }
.tip-line { line-height: 1.9; color: #7a5600; }
.action-row { display: flex; gap: 12px; margin-top: 22px; flex-wrap: wrap; }
@media (max-width: 980px) {
  .workspace-grid.two-cols { grid-template-columns: 1fr; }
  .path-actions, .action-row { justify-content: flex-start; }
}
</style>
